import { metrics } from '@opentelemetry/api'
import { useRef } from 'react'

import { useStore } from '~/store/store'
import { INSTRUMENTATION_NAME } from '~/utils/observability'

import { useLoadingObserver } from './useLoadingObserver'

/**
 * Set up meaningful distributions for user-perceived latency, rather
 * than the default histogram boundaries optimized for HTTP latency.
 */
const LOADING_DURATION_BUCKET_BOUNDARIES = [200, 500, 1000, 2000, 3000, 5000, 8000, 10000, 15000, 20000, 30000, 60000]

const meter = metrics.getMeter(INSTRUMENTATION_NAME)

const loadingDuration = meter.createHistogram('app.component.loading_duration', {
    description: 'Duration of component loading',
    advice: { explicitBucketBoundaries: LOADING_DURATION_BUCKET_BOUNDARIES },
    unit: 'ms',
})

type Props = {
    name: string
    isLoading: boolean
    queryKey?: string
}

/**
 * Instruments Otel metrics to capture user-perceived loading latency when fetching entirely new data that triggers
 * `isLoading` and blocks the UI, rather than background refetches.
 *
 * @param name - A name, typically in the format of `Page.ComponentName` (e.g. OP.WeekView.Calendar), to provide context for the metrics.
 * @param isLoading - A boolean indicating whether the component is currently loading data.
 * @param queryKey - A unique key used to track the query args.
 */
export const useOtelLoadingMetric = ({ name, isLoading, queryKey }: Props) => {
    const metricKey = queryKey ?? 'static'
    const startTimeRef = useRef<number | null>(null)

    useLoadingObserver(isLoading, metricKey, {
        onStart: () => {
            startTimeRef.current = performance.now()
        },
        onEnd: endCause => {
            if (!startTimeRef.current) return

            const duration = performance.now() - startTimeRef.current
            const issuer = useStore.getState().user.issuer
            const isDeepinsightUser = issuer === 'google' || issuer === 'mock'

            loadingDuration.record(
                duration,
                {
                    'di.component.path': name,
                    'di.user.is_deepinsight': isDeepinsightUser,
                    'di.metric.endCause': endCause,
                }
                // Add the context as the third arg to link the metric to its corresponding span once @opentelemetry/sdk-metrics supports `exemplars`:
                // globalActiveContext ?? context.active()
            )

            startTimeRef.current = null
        },
    })
}
